import { useEffect, useId, useState } from "react";

interface MermaidDiagramProps {
  chart: string;
  darkMode: boolean;
}

const sanitizeId = (id: string) => `mermaid-${id.replace(/[^a-zA-Z0-9_-]/g, "")}`;

export const MermaidDiagram = ({ chart, darkMode }: MermaidDiagramProps) => {
  const id = sanitizeId(useId());
  const [svg, setSvg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setError("");
    setSvg("");

    import("mermaid")
      .then(({ default: mermaid }) => {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: darkMode ? "dark" : "default",
          fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
          themeVariables: darkMode
            ? {
                background: "#171b21",
                primaryColor: "#1e293b",
                primaryTextColor: "#edf2f7",
                primaryBorderColor: "#475569",
                lineColor: "#94a3b8",
                secondaryColor: "#0f766e",
                tertiaryColor: "#0f172a",
              }
            : {
                background: "#ffffff",
                primaryColor: "#e0f2fe",
                primaryTextColor: "#121826",
                primaryBorderColor: "#93c5fd",
                lineColor: "#475569",
                secondaryColor: "#ccfbf1",
                tertiaryColor: "#f8fafc",
              },
        });

        return mermaid.render(`${id}-${Date.now()}`, chart);
      })
      .then(({ svg: renderedSvg }) => {
        if (!cancelled) setSvg(renderedSvg);
      })
      .catch((renderError: unknown) => {
        if (!cancelled) {
          setError(renderError instanceof Error ? renderError.message : "Could not render Mermaid diagram.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chart, darkMode, id]);

  if (error) {
    return (
      <figure className="mermaid-card mermaid-error">
        <figcaption>Mermaid diagram error</figcaption>
        <pre>{error}</pre>
      </figure>
    );
  }

  if (!svg) {
    return (
      <div className="mermaid-card mermaid-loading" aria-live="polite">
        Rendering diagram...
      </div>
    );
  }

  return <figure className="mermaid-card" aria-label="Mermaid diagram" dangerouslySetInnerHTML={{ __html: svg }} />;
};
